import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import './SchedulingDashboard.css';

function SchedulingDashboard() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysAhead, setDaysAhead] = useState(14);
  const [generating, setGenerating] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState(new Set());

  useEffect(() => {
    fetchSchedule();
    fetchSettings();
  }, []);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/scheduling/suggestions?days=${daysAhead}`);
      setSchedule(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
      setError('Failed to load schedule suggestions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/scheduling/settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    await fetchSchedule();
    setGenerating(false);
  };

  const handleAcceptBatch = async (daySchedule, batch) => {
    try {
      const response = await axios.post('/api/scheduling/accept', {
        fragranceId: batch.fragranceId,
        fragranceName: batch.fragranceName,
        workOrders: batch.workOrders,
        scheduledDate: daySchedule.date
      });

      alert(`Batch created successfully: ${response.data.batch.batch_name}`);

      // Refresh schedule
      await fetchSchedule();
    } catch (err) {
      console.error('Failed to accept batch:', err);
      alert('Failed to create batch from suggestion');
    }
  };

  const toggleBatchExpansion = (dayIndex, batchIndex) => {
    const key = `${dayIndex}-${batchIndex}`;
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleRemoveWorkOrder = async (dayIndex, batchIndex, workOrderId) => {
    if (!window.confirm('Remove this work order from the scheduled batch? It will be available for future scheduling.')) {
      return;
    }

    // Create updated schedule with work order removed
    const updatedSchedule = { ...schedule };
    const batch = updatedSchedule.schedule[dayIndex].batches[batchIndex];

    // Remove the work order
    batch.workOrders = batch.workOrders.filter(wo => (wo.id || wo.workOrderId) !== workOrderId);

    // If batch is now empty, remove it
    if (batch.workOrders.length === 0) {
      updatedSchedule.schedule[dayIndex].batches.splice(batchIndex, 1);

      // If day has no batches, remove the day
      if (updatedSchedule.schedule[dayIndex].batches.length === 0) {
        updatedSchedule.schedule.splice(dayIndex, 1);
      }
    } else {
      // Recalculate batch materials
      const OIL_PERCENTAGE = 0.09090909;
      let totalUnits = 0;
      let totalOilOz = 0;
      let totalWaxOz = 0;

      batch.workOrders.forEach(wo => {
        const quantity = wo.quantityRemaining || wo.quantity || 0;
        const caseQty = wo.caseQty || 1;
        const ozFill = wo.ozFill || 0;
        const units = quantity * caseQty;
        const totalWeightOz = units * ozFill;
        const oilOz = totalWeightOz * OIL_PERCENTAGE;
        const waxOz = totalWeightOz * (1 - OIL_PERCENTAGE);

        totalUnits += units;
        totalOilOz += oilOz;
        totalWaxOz += waxOz;
      });

      batch.materials = {
        totalUnits,
        totalOilLbs: (totalOilOz / 16).toFixed(2),
        totalWaxLbs: (totalWaxOz / 16).toFixed(2)
      };
    }

    // Recalculate totals
    updatedSchedule.totalDays = updatedSchedule.schedule.length;
    updatedSchedule.totalBatches = updatedSchedule.schedule.reduce((sum, day) => sum + day.batches.length, 0);

    setSchedule(updatedSchedule);
  };

  const formatDate = (dateString) => {
    // Parse as UTC to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
  };

  const getInventoryIcon = (status) => {
    switch(status) {
      case 'available':
        return '🟢';
      case 'insufficient':
        return '🔴';
      case 'on_order':
        return '🟡';
      default:
        return '⚪';
    }
  };

  const getInventoryLabel = (status) => {
    switch(status) {
      case 'available':
        return 'Oil Available';
      case 'insufficient':
        return 'Insufficient Oil';
      case 'on_order':
        return 'Oil On Order';
      default:
        return 'Unknown';
    }
  };

  const getPriorityBadge = (workOrders) => {
    // Find highest priority in work orders
    const priorities = workOrders.map(wo => wo.priority || 'Normal');
    if (priorities.includes('YOP') || priorities.includes('TOP')) return { label: 'TOP', color: '#9b4dca' };
    if (priorities.includes('Critical')) return { label: 'Critical', color: '#dc143c' };
    if (priorities.includes('High')) return { label: 'High', color: '#ff8c00' };
    return null;
  };

  if (loading) {
    return (
      <div className="scheduling-dashboard">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="scheduling-dashboard">
        <div className="error-message">{error}</div>
        <button onClick={handleGenerate} className="generate-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="scheduling-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h2>Batch Scheduling</h2>
          <p className="subtitle">
            Automated batch scheduling based on priorities, urgency, and constraints
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={handleGenerate}
            className="generate-btn"
            disabled={generating}
          >
            {generating ? 'Generating...' : '🔄 Regenerate Schedule'}
          </button>
        </div>
      </div>

      {schedule && (
        <>
          <div className="schedule-summary">
            <div className="summary-stat">
              <span className="stat-label">Days Scheduled</span>
              <span className="stat-value">{schedule.totalDays}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Total Batches</span>
              <span className="stat-value">{schedule.totalBatches}</span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Avg Batches/Day</span>
              <span className="stat-value">
                {(schedule.totalBatches / schedule.totalDays).toFixed(1)}
              </span>
            </div>
            <div className="summary-stat">
              <span className="stat-label">Generated</span>
              <span className="stat-value">
                {new Date(schedule.generatedAt).toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="schedule-table">
            <div className="table-header">
              <div className="col-date">Date</div>
              <div className="col-fragrance">Fragrance</div>
              <div className="col-units">Units</div>
              <div className="col-work-orders">Work Orders</div>
              <div className="col-oil">Oil (lbs)</div>
              <div className="col-status">Status</div>
              <div className="col-actions">Actions</div>
            </div>
            {schedule.schedule.map((daySchedule, dayIndex) => (
              <React.Fragment key={dayIndex}>
                {daySchedule.batches.map((batch, batchIndex) => {
                  const priorityBadge = getPriorityBadge(batch.workOrders);
                  const dailyTotal = daySchedule.batches.reduce((sum, b) => sum + b.materials.totalUnits, 0);
                  const batchKey = `${dayIndex}-${batchIndex}`;
                  const isExpanded = expandedBatches.has(batchKey);

                  return (
                    <React.Fragment key={batchKey}>
                      <div className="table-row">
                        <div className="col-date">
                          {batchIndex === 0 && (
                            <>
                              <div className="date-main">{formatDate(daySchedule.date)}</div>
                              <div className="date-total">{dailyTotal.toLocaleString()} units</div>
                            </>
                          )}
                        </div>
                        <div className="col-fragrance">
                          <div className="fragrance-info">
                            <span className="fragrance-name">{batch.fragranceName}</span>
                            {batch.totalScore && (
                              <span className="batch-score" title={`Total Score: ${batch.totalScore.toFixed(2)} (Avg: ${batch.score?.toFixed(2) || 'N/A'})`}>
                                Score: {batch.totalScore.toFixed(0)}
                              </span>
                            )}
                            {priorityBadge && (
                              <span
                                className="priority-badge"
                                style={{ backgroundColor: priorityBadge.color }}
                              >
                                {priorityBadge.label}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="col-units">
                          {batch.materials.totalUnits.toLocaleString()}
                        </div>
                        <div className="col-work-orders">
                          <button
                            onClick={() => toggleBatchExpansion(dayIndex, batchIndex)}
                            className="expand-btn"
                          >
                            {isExpanded ? '▼' : '▶'} {batch.workOrders.length} WOs
                          </button>
                        </div>
                        <div className="col-oil">
                          <div className="oil-info">
                            <div>{batch.materials.totalOilLbs} needed</div>
                            <div className="oil-available">{batch.oilAvailable} avail</div>
                          </div>
                        </div>
                        <div className="col-status">
                          <span
                            className={`status-indicator ${batch.inventoryStatus}`}
                            title={getInventoryLabel(batch.inventoryStatus)}
                          >
                            {getInventoryIcon(batch.inventoryStatus)} {batch.inventoryStatus}
                          </span>
                        </div>
                        <div className="col-actions">
                          <button
                            onClick={() => handleAcceptBatch(daySchedule, batch)}
                            className="create-batch-btn"
                            disabled={batch.inventoryStatus === 'insufficient'}
                          >
                            {batch.inventoryStatus === 'insufficient' ? 'Insufficient Oil' : 'Create Batch'}
                          </button>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="work-orders-expansion">
                          <div className="expansion-header">Work Orders in Batch</div>
                          <div className="expansion-content">
                            {batch.workOrders.map((wo, woIndex) => (
                              <React.Fragment key={woIndex}>
                                <div className="wo-expansion-row">
                                  <span className="wo-exp-num">{wo.workOrderNumber}</span>
                                  <span className="wo-exp-cust">{wo.customerName}</span>
                                  <span className="wo-exp-item">{wo.itemDescription || wo.itemSku}</span>
                                  <span className="wo-exp-qty">{wo.quantityRemaining || wo.quantity} cs</span>
                                  <span className="wo-exp-date">
                                    {wo.dateRequired ? new Date(wo.dateRequired).toLocaleDateString() : '-'}
                                  </span>
                                  <button
                                    onClick={() => handleRemoveWorkOrder(dayIndex, batchIndex, wo.id || wo.workOrderId)}
                                    className="remove-wo-btn"
                                    title="Remove from batch"
                                  >
                                    ✕
                                  </button>
                                </div>
                                {wo._scoreBreakdown && (
                                  <div className="wo-score-breakdown">
                                    <span className="score-total">Score: {wo._scoreBreakdown.total}</span>
                                    <span className="score-detail">Priority: {wo._scoreBreakdown.priority.label} ({wo._scoreBreakdown.priority.weighted})</span>
                                    <span className="score-detail">Category: {wo._scoreBreakdown.category.id} ({wo._scoreBreakdown.category.weighted})</span>
                                    <span className="score-detail">Urgency: {wo._scoreBreakdown.urgency.raw}/100 ({wo._scoreBreakdown.urgency.weighted})</span>
                                    <span className="score-detail">Inventory: {wo._scoreBreakdown.inventory.available ? '✓' : '✗'} ({wo._scoreBreakdown.inventory.weighted})</span>
                                    {wo._scoreBreakdown.keyAccountMultiplier?.applied && (
                                      <span className="score-detail key-account-multiplier">🔥 Key Account {wo._scoreBreakdown.keyAccountMultiplier.multiplier}x</span>
                                    )}
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {schedule.schedule.length === 0 && (
            <div className="empty-schedule">
              <p>No batches scheduled. Try regenerating with different settings.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SchedulingDashboard;
